import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Eye, EyeOff, UserPlus, Gift, Copy, Check } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';

const RegisterPage = () => {
    const navigate = useNavigate();
    const { register } = useAuth();
    const [loading, setLoading] = useState(false);
    const [showPassword, setShowPassword] = useState(false);
    const [welcomeCoupon, setWelcomeCoupon] = useState(null);
    const [copied, setCopied] = useState(false);
    const [formData, setFormData] = useState({
        name: '',
        email: '',
        password: '',
        phone: ''
    });

    const handleCopyCode = () => {
        navigator.clipboard.writeText(welcomeCoupon.code);
        setCopied(true);
        toast.success('Codice copiato!');
        setTimeout(() => setCopied(false), 2000);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        
        if (formData.password.length < 6) {
            toast.error('La password deve avere almeno 6 caratteri');
            return;
        }

        setLoading(true);
        
        try {
            const response = await register(formData.email, formData.password, formData.name, formData.phone);
            toast.success('Registrazione completata!');
            
            // Check if welcome coupon was returned
            if (response.welcome_coupon) {
                setWelcomeCoupon(response.welcome_coupon);
            } else {
                navigate('/');
            }
        } catch (error) {
            const detail = error.response?.data?.detail;
            const message = typeof detail === 'string' ? detail : 'Errore durante la registrazione';
            toast.error(message);
        } finally {
            setLoading(false);
        }
    };

    // Show welcome coupon modal
    if (welcomeCoupon) {
        return (
            <div className="min-h-screen flex items-center justify-center py-12 px-4">
                <div className="w-full max-w-md">
                    <div className="bg-surface border border-primary/50 rounded-2xl p-8 text-center animate-fade-in">
                        <div className="w-20 h-20 bg-primary/20 rounded-full flex items-center justify-center mx-auto mb-6">
                            <Gift className="w-10 h-10 text-primary" />
                        </div>
                        <h1 className="text-2xl font-bold mb-2">Benvenuto su G-Store!</h1>
                        <p className="text-muted mb-6">
                            Come regalo di benvenuto, hai ricevuto un coupon sconto del <span className="text-primary font-bold">10%</span> sul tuo primo ordine!
                        </p>
                        
                        <div className="bg-surface-elevated border border-border rounded-xl p-4 mb-6">
                            <p className="text-sm text-muted mb-2">Il tuo codice sconto:</p>
                            <div className="flex items-center justify-center gap-2">
                                <code className="text-2xl font-bold text-primary tracking-wider" data-testid="welcome-coupon-code">
                                    {welcomeCoupon.code}
                                </code>
                                <Button
                                    variant="ghost"
                                    size="icon"
                                    onClick={handleCopyCode}
                                    className="hover:bg-primary/20"
                                >
                                    {copied ? <Check className="w-5 h-5 text-success" /> : <Copy className="w-5 h-5" />}
                                </Button>
                            </div>
                            <p className="text-xs text-muted mt-2">Valido per 30 giorni</p>
                        </div>

                        <Button
                            className="w-full btn-primary"
                            onClick={() => navigate('/store')}
                            data-testid="go-to-store-btn"
                        >
                            Inizia a fare shopping
                        </Button>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen flex items-center justify-center py-12 px-4">
            <div className="w-full max-w-md">
                <div className="text-center mb-8">
                    <Link to="/" className="inline-block mb-4">
                        <span className="text-3xl font-bold text-primary">G-Store</span>
                    </Link>
                    <h1 className="text-2xl font-bold">Crea un account</h1>
                    <p className="text-muted mt-2">
                        Hai già un account?{' '}
                        <Link to="/login" className="text-primary hover:underline">
                            Accedi
                        </Link>
                    </p>
                    <div className="mt-4 p-3 bg-primary/10 border border-primary/30 rounded-lg">
                        <p className="text-sm flex items-center justify-center gap-2">
                            <Gift className="w-4 h-4 text-primary" />
                            <span>Registrati e ricevi un <strong className="text-primary">10% di sconto</strong> sul primo ordine!</span>
                        </p>
                    </div>
                </div>

                <form onSubmit={handleSubmit} className="bg-surface border border-border rounded-xl p-6 space-y-4">
                    <div>
                        <Label htmlFor="name">Nome completo</Label>
                        <Input
                            id="name"
                            type="text"
                            value={formData.name}
                            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                            placeholder="Mario Rossi"
                            className="bg-surface-elevated border-border mt-1"
                            required
                            data-testid="register-name"
                        />
                    </div>

                    <div>
                        <Label htmlFor="email">Email</Label>
                        <Input
                            id="email"
                            type="email"
                            value={formData.email}
                            onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                            placeholder="nome@email.com"
                            className="bg-surface-elevated border-border mt-1"
                            required
                            data-testid="register-email"
                        />
                    </div>

                    <div>
                        <Label htmlFor="phone">Telefono</Label>
                        <Input
                            id="phone"
                            type="tel"
                            value={formData.phone}
                            onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                            placeholder="+39 333 1234567"
                            className="bg-surface-elevated border-border mt-1"
                            data-testid="register-phone"
                        />
                    </div>

                    <div>
                        <Label htmlFor="password">Password</Label>
                        <div className="relative mt-1">
                            <Input
                                id="password"
                                type={showPassword ? 'text' : 'password'}
                                value={formData.password}
                                onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                                placeholder="••••••••"
                                className="bg-surface-elevated border-border pr-10"
                                required
                                minLength={6}
                                data-testid="register-password"
                            />
                            <button
                                type="button"
                                onClick={() => setShowPassword(!showPassword)}
                                className="absolute right-3 top-1/2 -translate-y-1/2 text-muted hover:text-white"
                            >
                                {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                            </button>
                        </div>
                        <p className="text-xs text-muted mt-1">Minimo 6 caratteri</p>
                    </div>

                    <Button
                        type="submit"
                        className="w-full btn-primary"
                        disabled={loading}
                        data-testid="register-submit"
                    >
                        {loading ? 'Registrazione...' : (
                            <>
                                <UserPlus className="w-4 h-4 mr-2" />
                                Registrati e ottieni il 10% di sconto
                            </>
                        )}
                    </Button>
                </form>
            </div>
        </div>
    );
};

export default RegisterPage;
